import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import { graphql, useLazyLoadQuery } from 'react-relay';
import styled from 'styled-components';
import {
  Box,
  ChoiceBox,
  ChoiceboxGroup,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { PlanOptionsQuery } from '__generated__/PlanOptionsQuery.graphql';
import { useQuery, useSegment } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils/query.utils';
import { UPFRONT_PAYMENT_ID } from '../../../../constants/ifp.constants';
import { useProductDetails } from '../../../../provider';
import { ProductDetailsActionTypes } from '../../../../reducer/productDetails.reducer';
import {
  IfpEligibilityCheckSteps,
  PlanBundleOptions,
  PrimaryCategory,
  WearableEligibilityCheckSteps,
} from '../../../../typings';
import { getValidOfferIds } from '../../../../utils/getValidOfferIds.utils';
import { useWearableDevice } from '../../hooks';
import SelectedPlanTile from './components/SelectedPlanTile';

export const ChoiceboxContainer = styled(Box)`
  max-width: 360px;
`;

const planOptionsQuery = graphql`
  query PlanOptionsQuery(
    $categoryId: String!
    $channel: CartChannel!
    $offerIds: [String!]!
  ) {
    relatedPlans(
      input: { categoryId: $categoryId, channel: $channel, offerIds: $offerIds }
    ) {
      bundles {
        id
        offerDetails {
          id
          name
          category {
            id
          }
          contentKey
          contractTerm {
            name
          }
          dataLayerCategory
          description
          price {
            basePrice
            basePriceExcludingTax
            effectivePrice
            effectivePriceExcludingTax
            frequency {
              period
              value
            }
            priceType
          }
          images {
            thumbnailUrl
          }
          entitlements {
            contentKey
            iconUrl
            longDescription
            name
            shortDescription
            sortOrder
            type
          }
          promotions {
            contentKey
            iconUrl
            longDescription
            name
            productType
            shortDescription
            sortOrder
          }
          productCharacteristics {
            balanceManagement
            isEligibilityRequired
          }
        }
      }
    }
  }
`;

const { ChooseNewPlan, UseExistingPlan, DeviceOnly } = PlanBundleOptions;
const { CheckEligibility, AddToCart } = IfpEligibilityCheckSteps;

const DeviceOnlyOption = () => {
  const { query, replaceQuery } = useQuery();
  const router = useRouter();
  const { dispatch, selectedDevice, planBundleOption } = useProductDetails();
  const { isWearable } = useWearableDevice();
  const ifpId = extractFirstValue(router.query.ifpId);
  const isPayUpfront = ifpId === UPFRONT_PAYMENT_ID;

  const setPlanBundleOption = useCallback(() => {
    dispatch({
      type: ProductDetailsActionTypes.SetPlanBundleOption,
      planBundleOption: DeviceOnly,
    });
  }, [dispatch]);

  const handlePlanOptionChange = () => {
    if (isWearable && !isPayUpfront) {
      replaceQuery({ ...query, ifpId: UPFRONT_PAYMENT_ID }).then(() => {
        setPlanBundleOption();
      });
    } else {
      setPlanBundleOption();
    }

    trackCustomLink('Buy device only');
  };

  return (
    <>
      {selectedDevice.soldSeparately ? (
        <ChoiceBox
          name="Device only"
          checked={planBundleOption === DeviceOnly}
          value={DeviceOnly}
          onChange={handlePlanOptionChange}
        >
          Buy device only
        </ChoiceBox>
      ) : null}

      {planBundleOption === DeviceOnly ? (
        <Box pt="s2">
          <Text variant="bodySMedium">
            Only need a device this time? No problem.
          </Text>
        </Box>
      ) : null}
    </>
  );
};

interface PlanOptionsInternalProps {
  deviceId: string;
}

const PlanOptionsInternal = ({ deviceId }: PlanOptionsInternalProps) => {
  const { channel } = useSegment();
  const { query, replaceQuery } = useQuery();
  const ifpId = extractFirstValue(query.ifpId);

  const {
    dispatch,
    ifpEligibility: {
      activeStep,
      isChangePlan,
      selectedLinePlanId,
      selectedLineNumber,
    },
    paymentOptions,
    planBundleOption,
    selectedDevice,
  } = useProductDetails();
  const { isWearable } = useWearableDevice();

  const { relatedPlans } = useLazyLoadQuery<PlanOptionsQuery>(
    planOptionsQuery,
    {
      categoryId: 'mobile',
      channel,
      offerIds: getValidOfferIds(deviceId, null, ifpId),
    },
  );

  const planOffers = relatedPlans?.bundles?.[0]?.offerDetails;
  const isPayUpfront = ifpId === UPFRONT_PAYMENT_ID;
  const { categoryIds } = selectedDevice;
  const isAccessory = categoryIds.includes(PrimaryCategory.ACCESSORIES);
  const isIfpIneligibleAccessories = isEmpty(paymentOptions) && isAccessory;
  const shouldHideNewPlanOption =
    (isAccessory && !isPayUpfront) || isEmpty(planOffers);
  const choiceBoxLabel = isWearable
    ? 'Your wearable plan'
    : 'Choose your mobile plan';

  const deviceAndPlanOptionText = isWearable
    ? 'Buy device and plan'
    : 'Choose a plan';

  const planOptionsRegionId = isWearable
    ? 'chooseYourWearablePlanTab'
    : 'chooseYourMobilePlanTab';

  const shouldDisplayPlans =
    planBundleOption === ChooseNewPlan ||
    (activeStep === IfpEligibilityCheckSteps.AddToCart &&
      selectedLineNumber === PlanBundleOptions.ChooseNewPlan) ||
    isChangePlan;

  const setPlanBundleOption = useCallback(
    (option: PlanBundleOptions) => {
      dispatch({
        type: ProductDetailsActionTypes.SetPlanBundleOption,
        planBundleOption: option,
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (!isPayUpfront) {
      if (isAccessory) {
        setPlanBundleOption(UseExistingPlan);
        return;
      }
      if (planBundleOption === DeviceOnly) {
        setPlanBundleOption(ChooseNewPlan);
      }
      return;
    }
    if (isAccessory && isEmpty(planOffers)) {
      setPlanBundleOption(DeviceOnly);
    }
  }, [
    isAccessory,
    isPayUpfront,
    planBundleOption,
    planOffers,
    setPlanBundleOption,
  ]);

  useEffect(() => {
    dispatch({
      type: ProductDetailsActionTypes.SelectPlan,
      planOffers,
    });
  }, [dispatch, planOffers]);

  if (isEmpty(planOffers) || isIfpIneligibleAccessories) {
    return null;
  }

  const handleOptionChange = (option: PlanBundleOptions) => {
    const isIfpEligibilityCheckRequired = option === UseExistingPlan;
    const isChooseNewPlan = option === ChooseNewPlan;
    const newActiveIfpEligibilityCheckStep = isIfpEligibilityCheckRequired
      ? CheckEligibility
      : AddToCart;

    if (isChooseNewPlan && !isWearable) {
      dispatch({
        type: ProductDetailsActionTypes.SetIfpEligibility,
        ifpEligibility: {
          activeStep: null,
          isCheckRequired: false,
          selectedLineNumber: null,
        },
        planBundleOption: option,
      });
      return;
    }

    if (isIfpEligibilityCheckRequired && selectedLinePlanId) {
      replaceQuery({
        ...query,
        planId: selectedLinePlanId,
      });
    }

    if (isWearable) {
      dispatch({
        type: ProductDetailsActionTypes.SetWearableEligibility,
        wearableEligibility: {
          activeStep: WearableEligibilityCheckSteps.CheckEligibility,
          isCheckRequired: true,
        },
        planBundleOption: option,
      });
    }

    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: isIfpEligibilityCheckRequired
          ? newActiveIfpEligibilityCheckStep
          : activeStep,
        isCheckRequired: isIfpEligibilityCheckRequired,
      },
      planBundleOption: option,
    });
    trackCustomLink(option);
  };

  return (
    <LinkRegion regionId={planOptionsRegionId}>
      <Text mb="s2" variant="subheadingM">
        {choiceBoxLabel}
      </Text>
      <ChoiceboxContainer mb="s4">
        <ChoiceboxGroup>
          {shouldHideNewPlanOption ? null : (
            <ChoiceBox
              name={deviceAndPlanOptionText}
              checked={planBundleOption === ChooseNewPlan}
              value={ChooseNewPlan}
              onChange={() => handleOptionChange(ChooseNewPlan)}
            >
              <Text variant="bodySMedium">{deviceAndPlanOptionText}</Text>
            </ChoiceBox>
          )}

          {isPayUpfront || isWearable ? (
            <DeviceOnlyOption />
          ) : (
            <ChoiceBox
              name="Use an existing Spark plan"
              checked={planBundleOption === UseExistingPlan}
              value={UseExistingPlan}
              onChange={() => handleOptionChange(UseExistingPlan)}
            >
              <Text variant="bodySMedium">Use an existing Spark plan</Text>
            </ChoiceBox>
          )}
        </ChoiceboxGroup>
      </ChoiceboxContainer>

      {shouldDisplayPlans ? (
        <Box mb={['s3', 's4']}>
          <SelectedPlanTile />
        </Box>
      ) : null}
    </LinkRegion>
  );
};

const PlanOptions = () => {
  const router = useRouter();
  const deviceId = extractFirstValue(router.query.offerId);

  if (!deviceId) {
    return null;
  }
  return <PlanOptionsInternal deviceId={deviceId} />;
};

export default PlanOptions;
